import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../../common/Forms/Input/Input';
import { notify } from '../../../../assets/helpers/toast';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as schemas from '../../../../assets/schemas/schemas';
import { ActionButton, AltButton } from '../../../common/Buttons/Buttons';
import {
  idCodes,
  genderOptions,
  civilStateOptions,
  gendersEnum,
} from '../../../../assets/helpers/options';
import { useParams } from 'react-router-dom';
import { customerService } from '../../../../services/customerService';
import { membershipService } from '../../../../services/membershipService';
import { LoadSpinner } from '../../../common/LoadSpinner/LoadSpinner';
import authService from '../../../../services/authService';
import {
  SalesDropzone,
  FileComponent,
} from '../../../common/Dropzone/Dropzone';
import { useDropzone } from 'react-dropzone';
import {
  capitalize,
  filterParams,
  validateAndSet,
  convertToBase64,
  convertBase64ToFile,
  mapCivilStateToEnum,
} from '../../../../assets/helpers/funciones';
import {
  idEnums,
  documentTypeEnum,
  idDocumentTypeOptions,
} from '../../../../assets/helpers/options';
import {
  preloadPersonalData,
  handleTestImageUpload,
} from './helpers/preloadFuntions';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { parseDateFlexible } from '../../../../assets/helpers/parsers';
import moment from 'moment';
import './LandingForms.scss';
import 'react-phone-number-input/style.css';

export const PersonalInfoForm = React.memo((props) => {
  const { setcurentStep, membership, setCustomer, curentStep } = props;
  const t = useTranslation();

  const [idUploaded, setIdUploaded] = useState('');
  const [isLoading, setIsloading] = useState(false);
  const [cedulaError, setCedulaError] = useState(false);
  const { saleChannel } = useParams();

  const formatResponse = (dataObj, documentType) => {
    if (!dataObj || !documentType) return {};

    // Candidate key forms: original, UPPER, camel->snake, snake->camel
    const candidates = [
      documentType,
      documentType.toUpperCase(),
      documentType.replace(/([A-Z])/g, '_$1').toLowerCase(),
      documentType.replace(/[_-](\w)/g, (_, c) => c.toUpperCase()),
    ];

    for (const k of candidates) {
      if (k && Object.prototype.hasOwnProperty.call(dataObj, k)) {
        return dataObj[k] || {};
      }
    }

    // Fallback: ignore separators and case (drivinglicense vs driving_license)
    const normalized = documentType.replace(/[_-]/g, '').toLowerCase();
    for (const key of Object.keys(dataObj)) {
      if (key && key.replace(/[_-]/g, '').toLowerCase() === normalized) {
        return dataObj[key] || {};
      }
    }

    return {};
  };

  const getDocumentsData = (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);

    formData.append('documentType', documentType);
    setIsloading(true);
    membershipService
      .getDocumentsDataV2(formData)
      .then((response) => {
        if ([200, 201].includes(response.status)) {
          const dataObj = response?.data || {};
          let res =
            formatResponse(dataObj, documentType) ||
            response?.data?.drivingLicense ||
            {};
          if (res && documentType === documentTypeEnum?.PASSPORT) {
            res = response?.data?.passport || dataObj;
            validateAndSet('identificationType', idEnums?.P, setValue, watch);
            validateAndSet(
              'identification',
              res?.passportNumber,
              setValue,
              watch
            );
            validateAndSet(
              'lastName',
              capitalize(res?.lastNames),
              setValue,
              watch
            );
            validateAndSet(
              'firstName',
              capitalize(res?.names),
              setValue,
              watch
            );
          }

          if (response?.data && documentType === documentTypeEnum?.RIF) {
            validateAndSet('identificationType', idEnums?.J, setValue, watch);
            validateAndSet(
              'identification',
              res?.rif?.replace('J', ''),
              setValue,
              watch
            );
            validateAndSet('firstName', res?.enterpriseName, setValue, watch);
            validateAndSet(
              'birthday',
              res?.registrationDate
                ? moment(res?.registrationDate, 'DD/MM/YYYY').toDate()
                : '',
              setValue,
              watch
            );
          }
          if (watch('identificationType') !== idEnums?.J) {
            validateAndSet(
              'firstName',
              capitalize(res?.names),
              setValue,
              watch
            );
            validateAndSet(
              'lastName',
              capitalize(res?.lastNames),
              setValue,
              watch
            );
            validateAndSet(
              'birthday',
              parseDateFlexible(res?.birthday ?? res?.birthDate),
              setValue,
              watch
            );
          }
          if (
            documentType === documentTypeEnum?.CEDULA &&
            res?.civilState !== null &&
            res?.civilState !== undefined
          ) {
            validateAndSet(
              'maritalStatus',
              mapCivilStateToEnum(res?.civilState),
              setValue,
              watch
            );
          }
          validateAndSet(
            'identificationType',
            capitalize(
              res?.nationality && documentType !== documentTypeEnum?.PASSPORT
                ? res?.nationality
                : watch('identificationType')
            ),
            setValue,
            watch
          );
          if (!watch('identification')) {
            validateAndSet(
              'identification',
              capitalize(res?.identification),
              setValue,
              watch
            );
          }
          validateAndSet(
            'gender',
            res?.gender === 'M' || res?.gender === 'MASCULINO'
              ? gendersEnum.MALE
              : res?.gender === 'F' || res?.gender === 'FEMENINO'
                ? gendersEnum.FEMALE
                : gendersEnum.OTHER,
            setValue,
            watch
          );

          if (
            response?.data &&
            documentType === response?.data?.drivingLicense
          ) {
            validateAndSet(
              'birthDate',
              response?.data?.inscriptionDate ||
                response?.data?.registrationDate
                ? moment(
                  response?.data?.inscriptionDate ||
                  response?.data?.registrationDate,
                  'DD/MM/YYYY'
                ).toDate()
                : '',
              setValue,
              watch
            );
          }
          if (
            watch('identificationType') &&
            watch('identification') &&
            watch('identification').length > 5
          ) {
            customerService
              .getCustomerById(
                watch('identification'),
                watch('identificationType')
              )
              .then((response) => {
                if (response.status === 200) {
                  if (
                    response?.data?.customer?.matchedMemberships?.length > 0
                  ) {
                    notify(
                      `Este cliente ya tiene una afiliación activa.`,
                      'info'
                    );
                  }
                }
              })
              .catch((error) => { });
          }
          setIsloading(false);
        } else {
          setIsloading(false);
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch((error) => {
        setIsloading(false);
      });
  };

  const onDrop1 = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        // Do whatever you want with the file contents
      };
      reader.readAsArrayBuffer(file);

      if (
        !['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(
          file.type
        )
      ) {
        notify('Agregar un formato valido de imagen', 'error');
        return null;
      }
      let maxWeight = 5;
      if (file.size * 0.000001 > maxWeight) {
        notify(`La imagen no debe superar los ${maxWeight} MB`, 'error');
      } else {
        setIdUploaded(file);
        getDocumentsData(file, watch('documentTypeImage'));
      }
    });
  }, []);

  const getCustomerById = () => {
    setValue('identification', watch('identification').replace(/\s/g, ''));

    if (
      watch('identificationType') &&
      watch('identification') &&
      watch('identification').length > 5
    ) {
      customerService
        .getCustomerById(watch('identification'), watch('identificationType'))
        .then((response) => {
          if (response.status === 200) {
            const {
              firstName,
              lastName,
              gender,
              maritalStatus,
              birthday,
              email,
              state,
              phonePrefix,
              broadcastChannel,
              instagram,
            } = response?.data?.customer;

            setValue('firstName', firstName);
            setValue('instagram', instagram);
            setValue('lastName', lastName);
            if (gender) {
              setValue('gender', gender);
            }
            if (maritalStatus) {
              setValue('maritalStatus', maritalStatus);
            }
            if (birthday) {
              setValue('birthday', new Date(birthday));
            }
            if (state) {
              setValue('state', state);
              setValue('broadcastChannel', broadcastChannel);
            }
            setValue('email', email?.toLowerCase());
            setValue('phonePrefix', phonePrefix);

            sessionStorage.setItem(
              'customer',
              JSON.stringify(response?.data?.customer)
            );
          }
          if (response.status === 404) {
            setValue('firstName', '');
            setValue('lastName', '');
            setValue('gender', '');
            setValue('birthday', '');
            setValue('state', '');
            setValue('broadcastChannel', '');
            setValue('email', '');
            setValue('phonePrefix', '');
            setValue('phoneNumber', '');
            setValue('instagram', '');
          }
        })
        .catch((error) => { });
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(schemas.CreateCustomerSchema(t)),
  });

  const onSubmit = handleSubmit(async (data) => {
    // Restart error colors on dropzone
    setCedulaError(false);

    if (data.phone) {
      const parsedPhone = parsePhoneNumberWithError(data.phone);
      data.phoneNumber = parsedPhone.nationalNumber;
      data.phonePrefix = '+' + parsedPhone.countryCallingCode;
    }

    if (data.optionalPhone) {
      const parsedOptionalPhone = parsePhoneNumberWithError(data.optionalPhone);
      data.optionalPhoneNumber = parsedOptionalPhone.nationalNumber;
      data.optionalPhonePrefix = '+' + parsedOptionalPhone.countryCallingCode;
    }

    data.lastName = capitalize(data?.lastName);
    data.email = data?.email?.toLowerCase();
    data.birthday = moment(data.birthday).format('YYYY-MM-DD');

    // No birthday for J, R, G identifications
    if (
      [idEnums?.J, idEnums?.R, idEnums?.G].includes(watch('identificationType'))
    ) {
      delete data.birthday;
    }

    delete data?.documentTypeImage;
    delete data?.countryCode;
    delete data?.phone;
    delete data?.optionalPhone;

    if (!idUploaded && !saleChannel) {
      notify('Adjunte su documento de identidad', 'error');
      setCedulaError(true);
      return null;
    }

    let fileTopost = appendFile(filterParams(data));
    let user = { ...data };

    const idBase64 = idUploaded ? await convertToBase64(idUploaded) : null;

    if (!isLoading) {
      if (!authService.getUser()) {
        const dataLayer = {
          event: 'funnel_step_personal_info',
          document_type: data?.identificationType + '-' + data?.identification,
        };
        // If logged In
        window.dataLayer.push(dataLayer);
      }

      setIsloading(true);
      customerService
        .createCustomer(fileTopost)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            setIsloading(false);

            // Store images in session storage

            let personalData = {
              idNameFileName: idUploaded?.name,
              documentTypeImage: watch('documentTypeImage'),
              idUploaded: idBase64,
              ...user,
            };

            sessionStorage.setItem(
              'personalData',
              JSON.stringify(personalData)
            );
            setCustomer(response?.data?._id);
            setcurentStep(3);
          } else {
            setIsloading(false);
            notify(`${response.message}` || 'error', 'error');
          }
        })
        .catch((error) => {
          setIsloading(false);
        });
    }
  });


  const appendFile = (data) => {
    const formData = new FormData();
    for (const key in data) {
      formData.append(key, data[key]);
    }

    formData.append('membership', membership?._id);

    if (saleChannel) {
      formData.append('landing', 'Marqueting qr');
    }
    if (idUploaded) {
      formData.append('dni', idUploaded);
    }

    return formData;
  };

  const prelaodData = () => {
    let personalData = sessionStorage.getItem('personalData');
    personalData = JSON.parse(personalData);
    let idUploaded = '';

    if (personalData) {
      // Read images from session storage

      idUploaded = convertBase64ToFile(
        personalData?.idUploaded,
        personalData.idNameFileName,
        'image/jpeg'
      );

      setIdUploaded(idUploaded);

      setValue('documentTypeImage', personalData?.documentTypeImage);
      setValue('firstName', personalData?.firstName);
      setValue('lastName', personalData?.lastName);
      setValue('phonePrefix', personalData?.phonePrefix);
      setValue('phoneNumber', personalData?.phoneNumber);
      setValue('phone', personalData?.phonePrefix + personalData?.phoneNumber);
      setValue(
        'optionalPhone',
        personalData?.optionalPhonePrefix + personalData?.optionalPhoneNumber
      );
      setValue('identificationType', personalData?.identificationType);
      setValue('identification', personalData?.identification);
      setValue('email', personalData?.email);
      setValue(
        'birthday',
        personalData?.birthday
          ? new Date(
            moment(personalData?.birthday)?.format(
              'ddd MMM DD YYYY 04:mm:ss ZZ'
            )
          )
          : ''
      );
      setValue('identificationType', personalData?.identificationType);
      setValue('gender', personalData?.gender);
      setValue('maritalStatus', personalData?.maritalStatus);

      setValue('broadcastChannel', personalData?.broadcastChannel);

      setValue('identificationType', personalData?.identificationType);
    }

  };
  const { getRootProps, getInputProps } = useDropzone({ onDrop: onDrop1 });

  useEffect(() => {
    prelaodData();
  }, []);

  let validateDisabled = () => {
    return !watch('identificationType') || !watch('identification');
  };

  let validateIfshowForm = () => {
    if (saleChannel) {
      return true;
    }
    if (watch('documentTypeImage')) {
      return true;
    } else return false;
  };

  const preloadPage = () => {
    handleTestImageUpload(
      onDrop1,
      `${process.env.PUBLIC_URL}/CedulaPrueba.jpeg`,
      'CedulaPrueba.jpeg'
    );
    preloadPersonalData(setValue);
  };

  return (
    <>
      <LoadSpinner show={isLoading} />
      <div className="landing-form-container">
        <h5
          onClick={
            ['true', true].includes(process.env.REACT_APP_DEVELOP)
              ? () => preloadPage()
              : ''
          }
        >
          Información Personal
        </h5>
        <p onClick={() => handleTestImageUpload()}>(*) Campos obligatorios</p>
        <br />
        <form className="responsive-landing-form">
          <div className="row">
            <Input
              bootstrap={'col-12 col-md-6'}
              label={'Tipo de Documento *'}
              type={'select'}
              capitalizeOff={true}
              name={'documentTypeImage'}
              register={register}
              placeholder={''}
              options={idDocumentTypeOptions}
              errors={errors?.documentTypeImage?.message}
            />
            <div className="col-6"></div>

            {validateIfshowForm() && (
              <div div className="col-12 col-md-6">
                {idUploaded ? (
                  <FileComponent
                    file={idUploaded}
                    remove={() => setIdUploaded('')}
                  />
                ) : (
                  <SalesDropzone
                    instructions={'Adjuntar Cédula de identidad / RIF *'}
                    maxWeight={'(JPG, PNG, PDF, máx. 3MB)'}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    message="Arrastrar o buscar Documento"
                    error={cedulaError}
                  />
                )}
              </div>
            )}
            {validateIfshowForm() && (
              <>
                <Input
                  bootstrap={'col-4 col-sm-2 col-md-2'}
                  label={'C.I / RIF*'}
                  type={'select'}
                  name={'identificationType'}
                  register={register}
                  placeholder={''}
                  options={idCodes}
                  errors={errors?.identificationType?.message}
                  onChange={() => setValue('identification', '')}
                />
                {[idEnums?.J, idEnums?.E, idEnums?.P]?.includes(
                  watch('identificationType')
                ) && (
                    <Input
                      bootstrap={'col-8 col-sm-4 col-md-4'}
                      label={''}
                      type={'text'}
                      name={'identification'}
                      register={register}
                      placeholder={''}
                      errors={errors?.identification?.message}
                      onChange={getCustomerById}
                      numpadForMobile={true}
                    />
                  )}
                {![idEnums?.J, idEnums?.E, idEnums?.P]?.includes(
                  watch('identificationType')
                ) && (
                    <Input
                      bootstrap={'col-8 col-sm-4 col-md-4'}
                      label={''}
                      type={'number'}
                      name={'identification'}
                      register={register}
                      placeholder={''}
                      errors={errors?.identification?.message}
                      onChange={getCustomerById}
                      numpadForMobile={true}
                    />
                  )}
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Nombre *'}
                  type={'text'}
                  name={'firstName'}
                  register={register}
                  placeholder={''}
                  errors={errors?.firstName?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Apellido *'}
                  type={'text'}
                  name={'lastName'}
                  register={register}
                  placeholder={''}
                  errors={errors?.lastName?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={`Fecha de Nacimiento DD/MM/AAAA ${![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                    watch('identificationType')
                  )
                    ? '*'
                    : ''
                    } `}
                  type={'date'}
                  name={'birthday'}
                  register={register}
                  control={control}
                  errors={errors?.birthday?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-4 col-sm-3 col-md-3'}
                  label={`Sexo  ${![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                    watch('identificationType')
                  )
                    ? '*'
                    : ''
                    }`}
                  type={'select'}
                  name={'gender'}
                  register={register}
                  placeholder={''}
                  options={genderOptions}
                  errors={errors?.gender?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-4 col-sm-3 col-md-3'}
                  label={`Estado Civil  ${![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                    watch('identificationType')
                  )
                    ? '*'
                    : ''
                    }`}
                  type={'select'}
                  name={'maritalStatus'}
                  register={register}
                  placeholder={''}
                  options={civilStateOptions}
                  errors={errors?.maritalStatus?.message}
                  disabled={validateDisabled()}
                />
                <div className="col-12"></div>
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Correo Electrónico *'}
                  type={'text'}
                  name={'email'}
                  register={register}
                  placeholder={''}
                  errors={errors?.email?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Correo Secundario'}
                  type={'text'}
                  name={'optionalEmail'}
                  register={register}
                  placeholder={''}
                  errors={errors?.optionalEmail?.message}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Teléfono *'}
                  type={'phone'}
                  control={control}
                  name={'phone'}
                  register={register}
                  placeholder={''}
                  errors={errors?.phone?.message}
                  numpadForMobile={true}
                  disabled={validateDisabled()}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Teléfono Secundario'}
                  type={'phone'}
                  control={control}
                  name={'optionalPhone'}
                  register={register}
                  placeholder={''}
                  errors={errors?.optionalPhone?.message}
                  disabled={validateDisabled()}
                  numpadForMobile={true}
                />
                <Input
                  bootstrap={'col-12 col-md-6'}
                  label={'Instagram'}
                  type={'text'}
                  name={'instagram'}
                  placeholder={''}
                  register={register}
                  errors={errors?.instagram?.message}
                />{' '}
                <col className="md-6" />
              </>
            )}
          </div>

          <div
            style={{ margin: '24px 0px 48px 0px' }}
            className="buttons-center-container"
          >
            <AltButton
              label={'Regresar'}
              action={() => setcurentStep(curentStep - 1)}
            />
            <ActionButton label={'Siguiente'} action={onSubmit} />
          </div>
        </form>
      </div>
    </>
  );
});
